import { useState } from 'react';
import { Modal, ModalBody, ModalHeader, RadioGroup } from '../ui';
import { SubjectForm } from './SubjectForm';
import type { DomainPack } from '../../lib/api/domains.gen';
import type { Subject } from '../../lib/api/subjects.gen';
import { SITES_REGISTRY_LABEL_SINGULAR } from '../../lib/vocab';

// "New site" (P03 §7.1): *"selects a domain only when more than one is active,
// then renders `domain.subjectSchema` through the generated contract + DS Form
// controls."*
//
// Both halves of that sentence are load-bearing and both are implemented
// literally here: with ONE active pack there is no picker at all (a
// single-domain org must never be asked a question with one answer), and with
// more than one the picker is the first thing rendered — the form cannot exist
// before its schema does. Switching packs REMOUNTS the form (`key={pack.key}`)
// rather than swapping a live resolver, so the controls and the zod schema can
// never be one pack out of step with each other.

export interface NewSubjectDialogProps {
  /** Packs a NEW record may use: the org's activated packs, or the default pack
   * when it has none (see `selectablePacks` in SitesPage — story 2-20's rule
   * that the base surface is never an entitlement). */
  activePacks: DomainPack[];
  onClose: () => void;
  onCreated: (subject: Subject) => void;
}

export function NewSubjectDialog({ activePacks, onClose, onCreated }: NewSubjectDialogProps): JSX.Element {
  const [selectedKey, setSelectedKey] = useState<string>(activePacks[0]?.key ?? '');
  const pack = activePacks.find((p) => p.key === selectedKey) ?? activePacks[0];
  const needsPicker = activePacks.length > 1;

  if (!pack) {
    // No activated pack: creating a record would have no shape to validate
    // against. Say so instead of rendering an empty form.
    return (
      <Modal onClose={onClose} label={`New ${SITES_REGISTRY_LABEL_SINGULAR.toLowerCase()}`}>
        <ModalHeader title="No domain is active" onClose={onClose} />
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            Records take their shape from an active domain pack. Ask an owner to enable one in Settings →
            Features, then try again.
          </p>
        </ModalBody>
      </Modal>
    );
  }

  const title = `New ${pack.subjectSchema.labels.singular.toLowerCase()}`;

  return (
    <Modal onClose={onClose} label={title}>
      {/* No ModalFooter: `SubjectForm` owns both its Cancel and `FormSubmit`
          buttons inside its own `<Form>` subtree (react-hook-form's
          `FormProvider` context does not extend past it), so the actions
          stay inside `ModalBody` rather than being lifted into a sibling
          footer region. */}
      <ModalHeader title={title} onClose={onClose}>
        <p className="text-sm text-muted-foreground">{`Add a ${pack.subjectSchema.labels.singular.toLowerCase()} to start inspecting it.`}</p>
      </ModalHeader>

      <ModalBody>
        {needsPicker && (
          // <fieldset>+<legend> is the same group chrome FormField's
          // `control="group"` mode renders — the picker is not an RHF field (it
          // decides WHICH form exists), so the chrome is written out here rather
          // than borrowed from a form it precedes.
          <fieldset className="mb-4 space-y-1.5" data-testid="domain-picker">
            <legend className="text-sm font-medium text-foreground">Domain</legend>
            <p className="text-xs text-muted-foreground">
              Each domain gives the record a different shape. Pick one to see its fields.
            </p>
            <RadioGroup
              name="domainKey"
              value={selectedKey}
              onChange={(event) => setSelectedKey(event.target.value)}
              options={activePacks.map((candidate) => ({
                value: candidate.key,
                label: candidate.subjectSchema.labels.singular,
              }))}
            />
          </fieldset>
        )}

        <SubjectForm key={pack.key} pack={pack} onCancel={onClose} onSaved={onCreated} />
      </ModalBody>
    </Modal>
  );
}
